// Mirror of app/src/schemas/crudMeta.ts (TECH-11) - the frontend validates the /generic_crud/meta
// and row responses against this schema at the boundary before rendering (META-05, TECH-11).
import { z } from "zod";

export const UiFieldType = z.enum(["string", "number", "currency", "bool", "date", "enum", "lookup"]);
export type UiFieldType = z.infer<typeof UiFieldType>;

export const ColumnMeta = z.object({
  field: z.string(),
  label: z.string(),
  type: UiFieldType,
  visibility: z.enum(["none", "default", "optional"]),
  mandatory: z.boolean(),
  displayOrder: z.number().nullable(),
  width: z.number().nullable(),
  editable: z.boolean(),
  minValue: z.number().nullable().optional(),
  maxValue: z.number().nullable().optional(),
  minLength: z.number().nullable().optional(),
  maxLength: z.number().nullable().optional(),
  regexPattern: z.string().nullable().optional(),
  refEntity: z.string().nullable().optional(),
  refDisplayField: z.string().nullable().optional(),
});
export type ColumnMeta = z.infer<typeof ColumnMeta>;

export const ChildMeta = z.object({
  childEntity: z.string(),
  fkColumn: z.string().nullable(),
  linkEntity: z.string().nullable(),
  linkParentColumn: z.string().nullable(),
  linkChildColumn: z.string().nullable(),
  tabLabel: z.string(),
  isManyToMany: z.boolean(),
});
export type ChildMeta = z.infer<typeof ChildMeta>;

export const ActionMeta = z.object({
  actionKey: z.string(),
  label: z.string(),
  icon: z.string().nullable(),
  actionType: z.enum(["create-child", "tag", "send-email", "navigate", "api-call"]),
  targetConfig: z.record(z.string(), z.unknown()).nullable(),
});
export type ActionMeta = z.infer<typeof ActionMeta>;

export const EntityMeta = z.object({
  name: z.string(),
  label: z.string(),
  labelSingular: z.string(),
  pk: z.string(),
  displayField: z.string(),
  deletePolicy: z.enum(["restrict", "cascade", "archive"]),
  editModeOptions: z.enum(["edit-inline", "edit-form", "edit-both"]),
  columns: z.array(ColumnMeta),
  children: z.array(ChildMeta),
  actions: z.array(ActionMeta),
});
export type EntityMeta = z.infer<typeof EntityMeta>;

export const NavNode = z.object({
  ref: z.string(),
  parentRef: z.string().nullable(),
  label: z.string(),
  entity: z.string().nullable(),
  filter: z.record(z.string(), z.string()).nullable(),
  icon: z.string().nullable(),
  sortOrder: z.number().nullable(),
});
export type NavNode = z.infer<typeof NavNode>;

export const MetadataGraph = z.object({
  entities: z.record(z.string(), EntityMeta),
  nav: z.array(NavNode),
});
export type MetadataGraph = z.infer<typeof MetadataGraph>;

export const RowsResponse = z.object({
  rows: z.array(z.record(z.string(), z.unknown())),
  filteredCount: z.number(),
  totalCount: z.number(),
});
export type RowsResponse = z.infer<typeof RowsResponse>;
